import { createContext, useContext, useState, useEffect } from "react";
import { login as apiLogin, logout as apiLogout } from "../api";
import { registerMobileDevice } from "../mobileDevice";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || "";

async function registerPush(token) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    const vapidRes = await fetch(`${API_URL}/api/push/vapid-key`);
    const { publicKey } = await vapidRes.json();
    const existing = await reg.pushManager.getSubscription();
    if (existing) { existing.unsubscribe(); }
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    const { endpoint, keys } = sub.toJSON();
    await fetch(`${API_URL}/api/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ endpoint, keys }),
    });
  } catch {}
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export const AuthProvider = ({ children }) => {
  const [agent, setAgent]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("midtc_agent");
    const token  = localStorage.getItem("midtc_token");
    if (stored && token) {
      setAgent(JSON.parse(stored));
      setLoading(false);
      registerMobileDevice(token); // phones only — no-op on desktop browsers
      // Background refresh to pick up profile changes (e.g. signature) since last login
      fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(a => { if (a) { localStorage.setItem("midtc_agent", JSON.stringify(a)); setAgent(a); } })
        .catch(() => {});
    } else if (token && !stored) {
      // Token seeded (desktop embed mode) but no cached agent — fetch profile
      fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(a => { if (a) { localStorage.setItem("midtc_agent", JSON.stringify(a)); setAgent(a); if ("Notification" in window && Notification.permission === "granted") registerPush(token).catch(() => {}); } })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      // Staging: auto-authenticate as admin without login page
      fetch(`${API_URL}/api/auth/staging-auto-login`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.token) {
            localStorage.setItem("midtc_token",         data.token);
            localStorage.setItem("midtc_refresh_token", data.refreshToken);
            localStorage.setItem("midtc_agent",         JSON.stringify(data.agent));
            setAgent(data.agent);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, []);

  const login = async (email, password) => {
    const res = await apiLogin(email, password);
    const { token, refreshToken, agent } = res.data;
    localStorage.setItem("midtc_token",        token);
    localStorage.setItem("midtc_refresh_token", refreshToken);
    localStorage.setItem("midtc_agent",         JSON.stringify(agent));
    setAgent(agent);
    registerMobileDevice(token); // phones only — no-op on desktop browsers
    if ("Notification" in window) {
      if (Notification.permission === "default") await Notification.requestPermission();
      if (Notification.permission === "granted") registerPush(token).catch(() => {});
    }
    return agent;
  };

  const loginWithToken = async (token) => {
    localStorage.setItem("midtc_token", token);
    const a = await fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : null);
    if (!a) throw new Error("Failed to fetch profile");
    localStorage.setItem("midtc_agent", JSON.stringify(a));
    setAgent(a);
    if ("Notification" in window && Notification.permission === "granted") registerPush(token).catch(() => {});
    return a;
  };

  const logout = async () => {
    try {
      const rt = localStorage.getItem("midtc_refresh_token");
      await apiLogout(rt);
    } catch {}
    localStorage.clear();
    setAgent(null);
    window.location.href = "/";
  };

  const updateAgent = (patch) => {
    setAgent(prev => {
      const updated = { ...prev, ...patch };
      localStorage.setItem("midtc_agent", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider value={{ agent, login, loginWithToken, logout, loading, updateAgent }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
